import React, { useEffect, useState } from "react";
import {
  Link,
  Flex,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Badge,
  Box,
  IconButton,
  useColorModeValue,
  Icon,
  Center,
  useColorMode,
} from "@chakra-ui/react";
import Image from "next/image";
import "./sideDrawer.css";
import { useRouter } from "next/router";
import { Logo } from "ui";
import { getRefrenceParams as getReferenceParams } from "app-head";

import hedera_light from "../assets/hedera_light.svg";
import hedera_dark from "../assets/hedera_dark.svg";
import { LiquidStaking } from "../LiquidStaking/LiquiStaking";
import { IIndexable } from "../constants";
import NextLink from "next/link";

export type subLinkProps = {
  title: string;
  link: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  tag?: string;
  disabled?: boolean;
  trackEvent?: () => void;
  // onClick: (data:any)=> void;
};
export type LinkProps = {
  title: string;
  link?: string;
  disabled?: boolean;
  iconKey: string;
  iconRight?: React.ReactNode;
  iconLeft?: React.ReactNode;
  subLink?: subLinkProps[];
  path?: string;
  // onClick: (data:any)=> void;
};

const ACCORDIAN_ICON_MAPPING: IIndexable = {
  hedera: {
    light: hedera_light,
    dark: hedera_dark,
  },
};

interface SideDrawerProps {
  onClick: (data: any) => void;
  sidenav: any;
  socials?: any;
  liquidStakingOptions: any;
  trackStaderLogoClick?: () => void;
  trackLiquidStakingClick: (key: string) => void;
  assetPrefix?: string;
}

export const SideDrawer = ({
  onClick,
  sidenav,
  socials,
  liquidStakingOptions,
  trackStaderLogoClick,
  trackLiquidStakingClick,
  assetPrefix = "",
}: SideDrawerProps) => {
  const bg = useColorModeValue("light", "dark");
  const { colorMode } = useColorMode();
  const router = useRouter();
  const [utmParams, setUTMParams] = useState<string>("");

  useEffect(() => {
    const { query } = router;
    const refParams = getReferenceParams();
    const x: any = {};

    for (let key in refParams) {
      if (query[key]) {
        x[key] = query[key];
      }
    }
    const search = new URLSearchParams(x).toString();
    setUTMParams(search === "" ? "" : `/?${search}`);
  }, [router]);

  const handleClick = (data: subLinkProps | LinkProps) => {
    onClick(data);
  };

  const getFocus = (item: LinkProps) => {
    if (router.pathname === "/") return true;
    if (item.link && router.asPath.includes(item.link)) return true;
    if (item.path && router.asPath.includes(item.path)) return true;
    return false;
  };

  const getActiveIndex = () => {
    for (let i = 0; i < sidenav.length; i++) {
      const item: LinkProps = sidenav[i];
      if (item.path && router.asPath.includes(item.path)) {
        return i;
      }
    }
    return 0;
  };

  const activeRouteClass = (item: any, index: number) => {
    if (router.pathname === "/" && index === 0) {
      return bg === "dark" ? "white" : "black";
    }
    if (router.asPath.includes(item.link)) {
      return bg === "dark" ? "white" : "black";
    } else {
      return "grey";
    }
  };

  return (
    <Box
      className={"sidedrawerContainer positionRightAndTop"}
      padding={{ base: "0", sm: "1rem 1.5rem 0px 2.5rem" }}
      overflow="auto"
    >
      <div>
        <Flex
          alignItems="center"
          mb="6"
          justifyContent={{ base: "space-between", xl: "flex-start" }}
          mt="2.12rem"
        >
          <Link
            href={`https://staderlabs.com${utmParams}`}
            _hover={{
              textDecoration: "none",
            }}
            onClick={() => {
              trackStaderLogoClick && trackStaderLogoClick();
            }}
          >
            <Flex alignItems="center">
              <Icon as={Logo} />
              <Text
                fontSize="1.5rem"
                color="brand.textColor"
                fontWeight="800"
                ml="1rem"
              >
                Stader
              </Text>
            </Flex>
          </Link>
        </Flex>

        <Box
          mt="2rem"
          bg="brand.backgroundLight"
          borderRadius="16px"
          // mx="-1rem"
        >
          {sidenav.map((item: LinkProps, index: number) => {
            return (
              <Accordion
                allowToggle
                key={index}
                defaultIndex={[getActiveIndex()]}
                p="16px 8px 24px 16px"
              >
                <AccordionItem border={0}>
                  <h2>
                    <AccordionButton
                      p={0}
                      _hover={{ backgroundColor: "transparent" }}
                      _focus={{ outlineColor: "nill" }}
                      onClick={() => (item.subLink ? null : handleClick(item))}
                      style={{ fontWeight: getFocus(item) ? "600" : "400" }}
                    >
                      <Center>
                        {item.iconKey ? (
                          <Center
                            bg="brand.primary"
                            _dark={{
                              bg: "#FFFFFF",
                            }}
                            borderRadius="50%"
                            width="20px"
                            height="20px"
                            border="1px solid"
                            borderColor="brand.separator"
                          >
                            <Image
                              src={
                                ACCORDIAN_ICON_MAPPING[item.iconKey][colorMode]
                              }
                              alt=""
                              height="12px"
                              width="12px"
                            />
                          </Center>
                        ) : null}
                        <Text ml="1rem">{item.title}</Text> {item.iconRight}
                      </Center>
                    </AccordionButton>
                  </h2>
                  {item.subLink && (
                    <AccordionPanel pb={0} ml="1.5rem">
                      {item.subLink.map(
                        (subItem: subLinkProps, index: number) => {
                          return (
                            <LinkWrapper
                              assetPrefix={assetPrefix}
                              key={index}
                              href={subItem.link}
                              disabled={subItem.disabled}
                              onClick={() => {
                                if (!subItem.disabled) {
                                  handleClick(subItem);
                                  subItem.trackEvent && subItem.trackEvent();
                                }
                              }}
                            >
                              <div className="sublink">
                                <IconButton
                                  aria-label={subItem.title}
                                  variant="link"
                                  disabled={subItem.disabled}
                                  style={{
                                    color: activeRouteClass(subItem, index),
                                  }}
                                  color="brand.primary"
                                  fontWeight="400"
                                  _disabled={{
                                    opacity: "1",
                                  }}
                                  _hover={{}}
                                  justifyContent="flex-start"
                                >
                                  <>
                                    <Text
                                      color={
                                        subItem.disabled
                                          ? "brand.disabledText"
                                          : "nill"
                                      }
                                    >
                                      {subItem.title}
                                    </Text>
                                    {subItem.tag && (
                                      <Badge
                                        ml={2}
                                        borderRadius={10}
                                        px={2}
                                        py={1}
                                        variant="solid"
                                        textTransform="none"
                                        background="brand.primary"
                                        color="brand.textLight"
                                      >
                                        {subItem.tag}
                                      </Badge>
                                    )}
                                  </>
                                </IconButton>
                              </div>
                            </LinkWrapper>
                          );
                        }
                      )}
                    </AccordionPanel>
                  )}
                </AccordionItem>
              </Accordion>
            );
          })}
        </Box>
      </div>

      <Box mt="2.5rem">
        <LiquidStaking
          stakingOptionsData={liquidStakingOptions}
          trackLiquidStakingClick={trackLiquidStakingClick}
        />
      </Box>

      {socials && (
        <Box mt="auto" position="relative" bottom="0">
          {socials}
        </Box>
      )}
    </Box>
  );
};

const LinkWrapper = (props: any) => {
  const router = useRouter();
  if (props.disabled) {
    return (
      <Box {...props} cursor="not-allowed">
        {props.children}
      </Box>
    );
  }
  return (
    <NextLink
      {...props}
      href={{ pathname: props.href, query: router.query }}
      as={{
        pathname: `/${props.assetPrefix}/${props.href}`,
        query: router.query,
      }}
    >
      {props.children}
    </NextLink>
  );
};
